import * as Papa from 'papaparse';
import type { ParsedData, Curve, Event } from '../../../interfaces/toolData';
import type { DTechFileConfig } from './types/DTechFileConfig';

import { DTechTimestampParser } from './utils/TimestampParser';
import { DTechXLSXHandler } from './utils/XLSXHandler';

/**
 * Unified D-Tech parser supporting multiple versions, data types, and formats
 * Implements ParsedData interface following established parser conventions
 * Supports both CSV and XLSX files with configuration-driven processing
 */
export class DTechUnifiedParser {
  
  /**
   * Parse CSV content using D-Tech configuration system
   * @param content CSV file content as string
   * @param filename Original filename for metadata and error reporting
   * @param config DTech file configuration to use for parsing
   * @returns ParsedData conforming to interface standards
   */
  parseCSV(content: string, filename: string = '', config: DTechFileConfig): ParsedData {
    try {
      // Basic validation
      if (!content || !content.trim()) {
        throw new Error('File is empty');
      }

      // Quick D-Tech file validation
      // ParserFactory should have already validated this is a D-Tech file

      // Parse CSV structure
      const lines = content.split('\n');
      const headerLine = lines[0];
      
      if (!headerLine) {
        throw new Error('No header line found');
      }

      // Use provided configuration (detection is handled by ParserFactory)

      // Parse CSV data using Papa Parse
      const parseResult = Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false // Keep as strings for consistent processing
      });

      if (parseResult.errors.length > 0) {
        console.warn('CSV parsing warnings:', parseResult.errors);
      }

      const rawData = parseResult.data as Record<string, string>[];
      
      // Process using provided configuration
      return this.processWithConfig(rawData, config, filename, parseResult.errors);

    } catch (error) {
      throw new Error(`Failed to parse D-Tech CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Parse XLSX content using D-Tech configuration system
   * @param buffer ArrayBuffer containing XLSX file data
   * @param filename Original filename for metadata and error reporting
   * @param config DTech file configuration to use for parsing
   * @returns ParsedData conforming to interface standards
   */
  parseXLSX(buffer: ArrayBuffer, filename: string = '', config: DTechFileConfig): ParsedData {
    try {
      // Validate XLSX format
      if (!DTechXLSXHandler.isValidXLSX(buffer)) {
        throw new Error('Invalid XLSX file format');
      }

      // Validate D-Tech XLSX structure
      const validation = DTechXLSXHandler.validateDTechXLSX(buffer);
      if (!validation.isValid) {
        throw new Error(`Invalid D-Tech XLSX file: ${validation.errors.join(', ')}`);
      }

      console.log(`📊 XLSX Info: ${validation.rowCount} rows, ${validation.columnCount} columns`);

      // Convert XLSX to CSV format for unified processing
      const csvContent = DTechXLSXHandler.parseXLSXToCSV(buffer, filename);
      
      // Process as CSV with provided config (reuse CSV parsing logic)
      return this.parseCSV(csvContent, filename, config);

    } catch (error) {
      throw new Error(`Failed to parse D-Tech XLSX file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Process parsed data using D-Tech configuration
   * Follows Pattern A: Basic validation only, let DataQualityService handle advanced cleaning
   */
  private processWithConfig(
    rawData: Record<string, string>[],
    config: DTechFileConfig,
    filename: string,
    parseErrors: any[]
  ): ParsedData {
    if (rawData.length === 0) {
      throw new Error('No data rows found after parsing');
    }

    // Parse timestamps using configuration
    const timeColumnName = this.findTimeColumn(rawData[0], config);
    if (!timeColumnName) {
      throw new Error(`Time column not found. Expected one of: ${config.timeColumns.join(', ')}`);
    }

    const timestampResult = DTechTimestampParser.parseTimestamps(
      rawData,
      timeColumnName,
      config
    );

    if (timestampResult.timestamps.length === 0) {
      throw new Error('No valid timestamps found in data');
    }

    const subSecondInfo = config.subSecondSampling ? ' with sub-second precision' : '';
    console.log(`⏰ Parsed ${timestampResult.timestamps.length}/${rawData.length} timestamps using ${timestampResult.detectedFormat} format${subSecondInfo}`);

    // Filter data to only include rows with valid timestamps
    const validData = timestampResult.validIndices.map(index => rawData[index]);
    const validTimestamps = timestampResult.timestamps;

    // Extract metadata
    const metadata = this.extractMetadata(filename, config, timestampResult);

    // Create curves from valid data
    const curves = this.createCurves(validData, validTimestamps, timeColumnName, config);

    // Create events from event columns (e.g., RSTC Version)
    const events = this.createEvents(validData, validTimestamps, config);

    // Prepare parsed data (following ParsedData interface)
    const parsedData: ParsedData = {
      toolType: config.toolType,
      fileType: config.fileType,
      curves,
      events: events.length > 0 ? events : undefined,
      metadata,
      dataPreview: {
        first5: validData.slice(0, 5),
        last5: validData.slice(-5),
        totalCount: validData.length
      },
      errors: [
        ...parseErrors.map(e => e.message || String(e)),
        ...timestampResult.parseErrors
      ].filter(Boolean)
    };

    return parsedData;
  }

  /**
   * Find the time column in the data
   */
  private findTimeColumn(sampleRow: Record<string, string>, config: DTechFileConfig): string | null {
    const availableColumns = Object.keys(sampleRow);
    
    for (const timeCol of config.timeColumns) {
      if (availableColumns.includes(timeCol)) {
        return timeCol;
      }
      
      // Try case-insensitive match
      const match = availableColumns.find(col => 
        col.toLowerCase().trim() === timeCol.toLowerCase().trim()
      );
      if (match) {
        return match;
      }
    }
    
    return null;
  }

  /**
   * Create time series curves from validated data
   * Following Pattern A: No direct data cleaning (let DataQualityService handle it)
   */
  private createCurves(
    data: Record<string, string>[],
    timestamps: Date[],
    timeColumnName: string,
    config: DTechFileConfig
  ): Curve[] {
    const curves: Curve[] = [];
    const headers = Object.keys(data[0] || {});
    
    // Create curves for all non-time, non-event columns
    const excludeColumns = [
      timeColumnName,
      ...(config.eventColumns || [])
    ];

    const dataColumns = headers.filter(header => 
      !excludeColumns.some(exclude => 
        header.toLowerCase().trim() === exclude.toLowerCase().trim()
      )
    );

    for (const columnName of dataColumns) {
      const yValues: number[] = [];
      const validTimestamps: Date[] = [];

      // Extract numeric values (let DataQualityService handle outliers)
      for (let i = 0; i < data.length; i++) {
        const valueStr = data[i][columnName];
        const value = this.parseNumericValue(valueStr);
        
        if (!isNaN(value)) {
          yValues.push(value);
          validTimestamps.push(timestamps[i]);
        }
        // Note: We skip NaN values but don't do statistical cleaning here
      }

      // Create curve if we have any valid data points
      if (yValues.length > 0) {
        curves.push({
          name: this.getDisplayName(columnName),
          xValues: validTimestamps,
          yValues: yValues,
          yUnit: this.getUnit(columnName)
        });
      }
    }

    return curves;
  }

  /**
   * Create events from event columns (e.g., RSTC Version changes)
   */
  private createEvents(
    data: Record<string, string>[],
    timestamps: Date[],
    config: DTechFileConfig
  ): Event[] {
    const events: Event[] = [];
    
    if (!config.eventColumns || config.eventColumns.length === 0) {
      return events;
    }

    for (const eventColumn of config.eventColumns) {
      const matchingColumn = Object.keys(data[0] || {}).find(col => 
        col.toLowerCase().trim() === eventColumn.toLowerCase().trim()
      );

      if (!matchingColumn) continue;

      const eventTimes: Date[] = [];
      const eventMessages: string[] = [];

      // Track value changes for events
      let lastValue: string | null = null;

      for (let i = 0; i < data.length; i++) {
        const currentValue = data[i][matchingColumn]?.trim();
        
        if (currentValue && currentValue !== lastValue) {
          eventTimes.push(timestamps[i]);
          eventMessages.push(`${eventColumn}: ${currentValue}`);
          lastValue = currentValue;
        }
      }

      if (eventTimes.length > 0) {
        events.push({
          name: eventColumn,
          xValues: eventTimes,
          yValues: eventMessages
        });
      }
    }

    return events;
  }

  /**
   * Parse numeric value with basic validation
   * No statistical outlier detection (handled by DataQualityService)
   */
  private parseNumericValue(valueStr: string): number {
    if (!valueStr || !valueStr.trim()) {
      return NaN;
    }

    const cleanValue = valueStr.trim();
    const parsed = parseFloat(cleanValue);
    
    // Basic validation only
    return isFinite(parsed) ? parsed : NaN;
  }

  /**
   * Get display name for column (could be enhanced with mapping)
   */
  private getDisplayName(columnName: string): string {
    // Simple cleanup for now (could be enhanced with ColumnNameMapper)
    return columnName.trim().replace(/['"]/g, '');
  }

  /**
   * Get unit for column based on common D-Tech patterns
   */
  private getUnit(columnName: string): string {
    const lowerName = columnName.toLowerCase();
    
    if (lowerName.includes('inclination') || lowerName.includes('azimuth') || lowerName.includes('angle')) {
      return 'DEG';
    }
    if (lowerName.includes('speed') || lowerName.includes('rpm')) {
      return 'RPM';
    }
    if (lowerName.includes('current')) {
      return 'Amps';
    }
    if (lowerName.includes('voltage')) {
      return 'Volts';
    }
    if (lowerName.includes('power')) {
      return 'W';
    }
    if (lowerName.includes('temperature')) {
      return 'C';
    }
    if (lowerName.includes('shock') || lowerName.includes('accel')) {
      return 'g';
    }
    if (lowerName.includes('%') || lowerName.includes('pwm')) {
      return '%';
    }
    
    return ''; // Unknown unit
  }

  /**
   * Extract metadata from parsing process
   */
  private extractMetadata(
    filename: string,
    config: DTechFileConfig,
    timestampResult: any
  ): Record<string, string> {
    return {
      filename,
      toolType: config.toolType,
      fileType: config.fileType,
      dtechVersion: config.version,
      dtechDataType: config.dtechDataType,
      timestampFormat: timestampResult.detectedFormat || config.timestampFormat,
      detectionConfidence: '95%', // Could be passed from detection result
      parsedTimestamps: String(timestampResult.timestamps.length),
      processingPattern: 'Pattern A (Centralized Cleaning)'
    };
  }
}